<template>
  <div class="helpers-busy-modal">
    <p class="helpers-busy-modal__intro">{{ introText }}</p>

    <div class="helpers-busy-modal__blockers">
      <div v-for="blocker in blockers" :key="blocker.userId" class="helpers-busy-modal__blocker">
        <p v-if="blockers.length > 1" class="helpers-busy-modal__blocker-name">
          {{ blocker.name }}
        </p>
        <ul class="helpers-busy-modal__appointments">
          <li
            v-for="appointment in blocker.appointments"
            :key="appointment.id"
            class="helpers-busy-modal__appointment"
          >
            <span class="helpers-busy-modal__dot"></span>
            <span class="helpers-busy-modal__time">
              {{ appointment.start }} – {{ appointment.end }}
            </span>
            <span class="helpers-busy-modal__meta">
              <template v-if="appointment.number">{{ appointment.number }}</template>
              <template v-if="appointment.primaryName && appointment.primaryName !== blocker.name">
                <template v-if="appointment.number">·</template>
                {{
                  $t('planning_page.helpers_busy.with_technician', {
                    name: appointment.primaryName,
                  })
                }}
              </template>
            </span>
          </li>
        </ul>
      </div>
    </div>

    <div class="helpers-busy-modal__options" role="radiogroup">
      <label
        v-for="option in options"
        :key="option.value"
        class="helpers-busy-modal__option"
        :class="{ 'helpers-busy-modal__option--active': choice === option.value }"
      >
        <input
          v-model="choice"
          type="radio"
          name="helpers-busy-choice"
          :value="option.value"
          class="helpers-busy-modal__radio"
        />
        <span class="helpers-busy-modal__option-body">
          <span class="helpers-busy-modal__option-label">{{ option.label }}</span>
          <span class="helpers-busy-modal__option-hint">{{ option.hint }}</span>
        </span>
      </label>
    </div>

    <div class="helpers-busy-modal__actions">
      <Btn
        variant="secondary"
        :text="$t('planning_page.helpers_busy.cancel')"
        :disabled="submitting"
        @click="cancel"
      />
      <Btn
        variant="primary"
        icon="calendar"
        :text="$t('planning_page.helpers_busy.submit')"
        :loading="submitting"
        @click="submit"
      />
    </div>
  </div>
</template>

<script>
  import { computed, getCurrentInstance, ref } from 'vue';
  import { useUIStore } from '@/store/uiStore';
  import Btn from '@/components/partials/Btn.vue';

  export default {
    name: 'PlanningHelpersBusyModal',
    components: { Btn },
    props: {
      modalId: { type: [String, Number], default: null },
      technicianLabel: { type: String, default: null },
      dateLabel: { type: String, required: true },
      blockers: { type: Array, required: true },
      withoutHelpers: { type: Object, required: true },
      onSubmit: { type: Function, required: true },
    },
    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const uiStore = useUIStore();

      const choice = ref('without');
      const submitting = ref(false);

      const helperNames = computed(() => props.blockers.map((b) => b.name).join(', '));

      const introText = computed(() => {
        const params = {
          technician: props.technicianLabel,
          date: props.dateLabel,
          time: props.withoutHelpers.start,
          helpers: helperNames.value,
        };
        return props.technicianLabel
          ? $t('planning_page.helpers_busy.intro', params)
          : $t('planning_page.helpers_busy.intro_unassigned', params);
      });

      const options = computed(() => {
        const helpers = helperNames.value;
        const technician = props.technicianLabel || '—';
        const range = `${props.withoutHelpers.start} – ${props.withoutHelpers.end}`;
        return [
          {
            value: 'without',
            label: $t('planning_page.helpers_busy.option_without', { helpers }),
            hint: $t(
              props.withoutHelpers.moved
                ? 'planning_page.helpers_busy.option_without_hint_moved'
                : 'planning_page.helpers_busy.option_without_hint',
              { technician, range }
            ),
          },
          {
            value: 'overlap',
            label: $t('planning_page.helpers_busy.option_overlap', { helpers }),
            hint: $t('planning_page.helpers_busy.option_overlap_hint', { helpers, range }),
          },
        ];
      });

      const cancel = () => uiStore.closeModal(props.modalId);

      const submit = async () => {
        submitting.value = true;
        try {
          await props.onSubmit(choice.value);
        } finally {
          submitting.value = false;
        }
      };

      return { choice, submitting, introText, options, cancel, submit };
    },
  };
</script>

<style scoped lang="scss">
  .helpers-busy-modal {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &__intro {
      margin: 0;
      font-size: 0.875rem;
      line-height: 1.5;
      color: var(--slate-700);
    }

    &__blockers {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      padding: 0.75rem;
      border-radius: 0.5rem;
      background: var(--slate-50);
      border: 1px solid var(--slate-200);
    }

    &__blocker-name {
      margin: 0 0 0.375rem;
      font-size: 0.75rem;
      font-weight: 600;
      color: var(--slate-600);
    }

    &__appointments {
      display: flex;
      flex-direction: column;
      gap: 0.375rem;
      margin: 0;
      padding: 0;
      list-style: none;
    }

    &__appointment {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.8125rem;
    }

    &__dot {
      flex-shrink: 0;
      width: 0.5rem;
      height: 0.5rem;
      border-radius: 50%;
      background: var(--amber-500);
    }

    &__time {
      font-weight: 600;
      font-variant-numeric: tabular-nums;
      color: var(--slate-800);
    }

    &__meta {
      @include ellipsis(1);
      color: var(--slate-500);
    }

    &__options {
      display: flex;
      flex-direction: column;
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;
      overflow: hidden;
    }

    &__option {
      display: flex;
      align-items: flex-start;
      gap: 0.625rem;
      padding: 0.75rem;
      cursor: pointer;
      transition: background-color 0.15s;

      & + & {
        border-top: 1px solid var(--slate-200);
      }

      @include hover {
        background: var(--slate-50);
      }

      &--active {
        background: var(--brand-50);

        @include hover {
          background: var(--brand-50);
        }
      }
    }

    &__radio {
      margin: 0.1875rem 0 0;
      accent-color: var(--brand-600);
    }

    &__option-body {
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
    }

    &__option-label {
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--slate-800);
    }

    &__option-hint {
      font-size: 0.75rem;
      color: var(--slate-500);
    }

    &__actions {
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
      padding-top: 1rem;
      border-top: 1px solid var(--slate-200);
    }
  }
</style>
